import { format } from 'date-fns-tz';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    addUnitsOfTime,
    autoinject,
    bind, DateRangePicker,
    Enums,
    FormStore,
    inject,
    NothingHere, SubTopBar,
    subUnitsOfTime,
} from 'Mri.Web.Common';
import * as React from 'react';
import { BsCardHeading } from '@react-icons/all-files/bs/BsCardHeading';
import { RouteComponentProps } from 'react-router';
import { AppStore } from '../../Core/AppStore';
import { DisplaysStore } from '../DisplaysStore';
import {Form} from "reactstrap";

export interface IDisplayAuditLogParams {
    serialNumber: string;
}
export interface IDisplayAuditLogProps extends RouteComponentProps<IDisplayAuditLogParams> {}

@autoinject
@observer
export class DisplayAuditLog extends React.Component<IDisplayAuditLogProps, any> {
    public constructor(props: IDisplayAuditLogProps) {
        super(props);
        this.state = {};

        const endTime = new Date();
        const startTime = subUnitsOfTime(new Date(), 1, 'day');

        this.formStore.fields.startTime.setValue(startTime);
        this.formStore.fields.endTime.setValue(endTime);
    }
    @action
    public async componentDidMount() {
        this.loading = true;

        try {
            await this.displaysStore.loadAsync(this.props.match.params.serialNumber);
            await this.getAuditLogAsync();
        } catch (e: any) {
            this.appStore.setError(e);
        } finally {
            this.loading = false;
        }
    }

    public render() {
        return (
            <div>
                <SubTopBar>
                    <Form onSubmit={this.onSubmit}>
                        <div className="d-block w-100">
                            <DateRangePicker
                                startDate={this.formStore.fields.startTime}
                                endDate={this.formStore.fields.endTime}
                                className="d-inline-block me-2"
                                timezone={this.displaysStore.timezones.get(this.props.match.params.serialNumber)}
                            />

                            <button className="mx-2 btn btn-primary">{this.loading ? 'Loading...' : 'Apply'}</button>
                        </div>
                    </Form>
                </SubTopBar>
                <div>
                    <table className="table table-sm">
                        <thead>
                            <tr>
                                <th scope="col">Event</th>
                                <th scope="col">Date</th>
                                <th scope="col">Details</th>
                            </tr>
                        </thead>
                        <tbody>{this.auditLogs?.map(this.renderAuditLog)}</tbody>
                    </table>

                    <NothingHere loading={this.loading} empty={!this.auditLogs?.length} icon={<BsCardHeading />} />
                </div>
            </div>
        );
    }

    public renderAuditLog(auditLog: Mri.Audit.AuditLog<any>, key: any) {
        switch (auditLog.type) {
            case Mri.Audit.ActionType.FileSystemUpdateLog:
                const status = auditLog.payload.status as Mri.DisplaySoftware.FileSystemUpdateStatus;
                return (
                    <tr key={key}>
                        <td scope="row">Firmware Update {status.status}</td>
                        <td>
                            {format(auditLog.timestamp, 'Pp z')} ({})
                        </td>
                        <td scope="row">{status.targetFileSystem.fileName}</td>
                    </tr>
                );
            case Mri.Audit.ActionType.DisplayConnectionDataUpdateLog:
                const dcd = auditLog.payload as Mri.Audit.DisplayConnectionDataUpdateLog;
                const server = dcd.nocServer == 'Global' ? 'MRI VPN' : dcd.nocServer == null ? '-' : dcd.nocServer;
                const source = dcd.sourceNetwork
                    ? ` via ${Enums.toString(Enums.SourceNetwork, dcd.sourceNetwork)} network`
                    : '';
                const networkString = `${dcd.displayBoardIP ?? '-'} on ${server}${source}`;

                return (
                    <tr key={key}>
                        <td scope="row">IP Changed</td>
                        <td>{format(auditLog.timestamp, 'Pp z')}</td>
                        <td scope="row">{networkString}</td>
                    </tr>
                );
            case Mri.Audit.ActionType.WebApiKickLog:
                const wkl = auditLog.payload as Mri.Audit.WebApiKickLog;

                return (
                    <tr key={key}>
                        <td scope="row">WebApi Kicked</td>
                        <td>{format(auditLog.timestamp, 'Pp z')}</td>
                        <td scope="row">{wkl.email}</td>
                    </tr>
                );
            case Mri.Audit.ActionType.RebootSystemLog:
                const rsl = auditLog.payload as Mri.Audit.RebootSystemLog;

                return (
                    <tr key={key}>
                        <td scope="row">System Rebooted</td>
                        <td>{format(auditLog.timestamp, 'Pp z')}</td>
                        <td scope="row">{rsl.email}</td>
                    </tr>
                );
            case Mri.Audit.ActionType.ResetSystemLog:
                const resetsl = auditLog.payload as Mri.Audit.ResetSystemLog;

                return (
                    <tr key={key}>
                        <td scope="row">Server Restart</td>
                        <td>{format(auditLog.timestamp, 'Pp z')}</td>
                        <td scope="row">{resetsl.email}</td>
                    </tr>
                );
            case Mri.Audit.ActionType.StandbyModeLog:
                const sml = auditLog.payload as Mri.Audit.StandbyModeLog;

                return (
                    <tr key={key}>
                        <td scope="row">{sml.enabled ? "Standby Mode Enabled" : "Standby Mode Disabled"}</td>
                        <td>{format(auditLog.timestamp, 'Pp z')}</td>
                        <td scope="row">{sml.email}</td>
                    </tr>
                );
            default:
                return (
                    <tr key={key}>
                        <td scope="row">{auditLog.type}</td>
                        <td>{format(auditLog.timestamp, 'Pp z')}</td>
                    </tr>
                );
        }
    }

    @bind
    private onSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        this.getAuditLogAsync();
    }

    @bind
    @action
    private async getAuditLogAsync() {
        this.loading = true;
        let startTime = new Date(this.formStore.fields.startTime.value);
        let endTime = new Date(this.formStore.fields.endTime.value);
        let oneYearAgo =  subUnitsOfTime(new Date(), 1, 'years');
        let dayInFuture = addUnitsOfTime(new Date(), 1, 'day');

        if(startTime < oneYearAgo) startTime = subUnitsOfTime(new Date(), 1, 'day');
        if(endTime > dayInFuture) endTime = new Date();

        try {
            await this.displaysStore.loadDisplayActionLogsByTimeAsync(
                this.props.match.params.serialNumber,
                startTime,
                endTime
            );
        } catch (e: any) {
            this.appStore.setError(e);
        } finally {
            this.loading = false;
        }
    }


    @computed
    private get auditLogs() {
        const events = this.displaysStore.auditLogs.get(this.props.match.params.serialNumber);
        if (events != undefined) {
            return events.sort((a, b) => +new Date(b.timestamp) - +new Date(a.timestamp));
        }
        return events;
    }

    private formStore = new FormStore({
        startTime: new Date(),
        endTime: new Date(),
    });
    @observable
    private loading = false;
    @inject
    private appStore!: AppStore;
    @inject
    private displaysStore!: DisplaysStore;
}
